// Technologies shown under "Technology Stacks" on case studies. The live pages
// use small raster logos (many with odd backgrounds or meaningless filenames);
// they are matched by filename and rendered as consistent chips instead.
// Brand marks come from Simple Icons; Microsoft and AWS marks are not
// available there, so those use a fitting Lucide icon in the brand colour.
import {
  AppWindow,
  Cable,
  Cloud,
  Coffee,
  Database,
  Network,
  Package,
  Server,
  Smartphone,
  SquareTerminal,
} from "lucide-react";
import {
  siAngular,
  siCplusplus,
  siDotnet,
  siMongodb,
  siMqtt,
  siMui,
  siNestjs,
  siNextdotjs,
  siNodedotjs,
  siOpengl,
  siPostgresql,
  siPrisma,
  siReact,
  siSpringboot,
  siTypescript,
  siWebgl,
} from "simple-icons";
import { imageKey } from "../lib/content-blocks";

const brand = (name, icon, color = `#${icon.hex}`) => ({
  name,
  path: icon.path,
  color,
});
const lucide = (name, Icon, color) => ({ name, Icon, color });

/** Original image filename (size suffix removed) → technology. */
export const techByFile = {
  "postgre-sql-logo": brand("PostgreSQL", siPostgresql),
  "reactjs-logo": brand("React", siReact, "#087EA4"),
  "react-mui-logo": brand("Material UI", siMui),
  "next-js-logo": brand("Next.js", siNextdotjs),
  "typescript-logo-2": brand("TypeScript", siTypescript),
  "nest-js": brand("NestJS", siNestjs),
  "nodejs-logo": brand("Node.js", siNodedotjs),
  "prisma-logo": brand("Prisma", siPrisma),
  "loadtraxx-technology-stacks-4": brand("Node.js", siNodedotjs),
  "aws-logo": lucide("AWS", Cloud, "#FF9900"),
  "loadtraxx-technology-stacks": lucide("Azure SQL", Database, "#0078D4"),
  "loadtraxx-technology-stacks-1": lucide(
    "Azure API Management",
    Cloud,
    "#0078D4",
  ),
  "loadtraxx-technology-stacks-2": lucide(
    "Azure App Services",
    Cloud,
    "#0078D4",
  ),
  "loadtraxx-technology-stacks-3": lucide("Xamarin", Smartphone, "#3498DB"),
  "website-logosicons": lucide("SQL Server", Database, "#CC2927"),
  1: brand("OpenGL", siOpengl),
  13: brand("Angular", siAngular, "#DD0031"),
  15: brand(".NET", siDotnet),
  "16-1": brand("Spring Boot", siSpringboot),
  2: brand("C++", siCplusplus),
  3: lucide("CLI", SquareTerminal, "#12232B"),
  "30-day-24": lucide("Java", Coffee, "#E76F00"),
  35: lucide("Windows Server", Server, "#0078D4"),
  4: lucide("Windows Installer", Package, "#0078D4"),
  5: lucide("Windows Forms", AppWindow, "#0078D4"),
  "50-1": brand("WebGL", siWebgl),
  "52-1": brand("MQTT", siMqtt),
  52: brand(".NET Core", siDotnet),
  58: brand("MongoDB", siMongodb),
  6: lucide("RS-232 Serial", Cable, "#2E7D4F"),
  7: lucide("CAN bus", Network, "#1F5FA8"),
};

/** The technology an image shows, by its filename; undefined if unknown. */
export const techFor = (src) => techByFile[imageKey(src).replace(/\.\w+$/, "")];

/** Unique technologies by name, in the order given (Services page toolbox). */
export const stack = [
  "React",
  "TypeScript",
  "Next.js",
  "Node.js",
  "NestJS",
  ".NET",
  "Angular",
  "Java",
  "C++",
  "PostgreSQL",
  "SQL Server",
  "MongoDB",
  "Prisma",
  "AWS",
  "Azure App Services",
  "Xamarin",
  "MQTT",
  "CAN bus",
].map((name) => Object.values(techByFile).find((tech) => tech.name === name));
